'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';

import type { DanceTemplate as CardDanceTemplate } from '@/config/dance-templates';
import type { TemplateBrowserProps } from '@/shared/blocks/common/template-browser';
import { cn } from '@/shared/lib/utils';
import type {
  PublicDanceTemplate,
  PublicDanceTemplateFacet,
} from '@/shared/models/dance-template';

interface DanceTemplatesClientProps {
  section?: {
    id?: string;
    label?: string;
    title?: string;
    description?: string;
    className?: string;
    defaultSubjectType?: string;
  };
  initialTemplates: PublicDanceTemplate[];
  initialSubjectType?: string;
  facets: {
    subjectTypes: PublicDanceTemplateFacet[];
    durations: PublicDanceTemplateFacet[];
  };
  initialPagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

type LoadedTemplateBrowser = (
  props: TemplateBrowserProps
) => ReactElement | null;

function TemplateBrowserSkeleton() {
  return (
    <div className="space-y-6">
      <div className="border-border/70 bg-card/60 space-y-4 rounded-lg border px-4 py-4">
        <div className="bg-muted h-9 animate-pulse rounded-md" />
        <div className="bg-muted h-9 animate-pulse rounded-md" />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 10 }).map((_, index) => (
          <div
            key={index}
            className="bg-muted aspect-[9/16] animate-pulse rounded-2xl"
          />
        ))}
      </div>
    </div>
  );
}

export function DanceTemplatesClient({
  section,
  initialTemplates,
  initialSubjectType = '',
  facets,
  initialPagination,
}: DanceTemplatesClientProps) {
  const handleSelect = useCallback((template: CardDanceTemplate) => {
    window.dispatchEvent(
      new CustomEvent('select-dance-template', { detail: template })
    );

    const generator = document.getElementById('generator');
    if (generator) {
      generator.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const browserRef = useRef<HTMLDivElement>(null);
  const [shouldLoadBrowser, setShouldLoadBrowser] = useState(false);
  const [TemplateBrowser, setTemplateBrowser] =
    useState<LoadedTemplateBrowser | null>(null);

  useEffect(() => {
    const root = browserRef.current;
    if (!root || shouldLoadBrowser) return;

    if (!('IntersectionObserver' in window)) {
      setShouldLoadBrowser(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadBrowser(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px', threshold: 0.3 }
    );

    observer.observe(root);
    return () => observer.disconnect();
  }, [shouldLoadBrowser]);

  useEffect(() => {
    if (!shouldLoadBrowser || TemplateBrowser) return;

    let cancelled = false;
    void import('@/shared/blocks/common/template-browser').then((mod) => {
      if (!cancelled) {
        setTemplateBrowser(() => mod.TemplateBrowser);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [TemplateBrowser, shouldLoadBrowser]);

  return (
    <section
      id={section?.id}
      className={cn('py-16 md:py-24', section?.className)}
    >
      <div className="container mb-10 text-center md:mb-14">
        {section?.label && (
          <span className="text-primary text-sm font-bold tracking-wider uppercase">
            {section.label}
          </span>
        )}
        {section?.title && (
          <h2 className="mx-auto mt-4 mb-4 max-w-4xl text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
            {section.title}
          </h2>
        )}
        {section?.description && (
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
            {section.description}
          </p>
        )}
      </div>

      <div ref={browserRef} className="container">
        {TemplateBrowser ? (
          <TemplateBrowser
            initialTemplates={initialTemplates}
            initialSubjectType={initialSubjectType}
            facets={facets}
            initialPagination={initialPagination}
            onSelect={handleSelect}
          />
        ) : (
          <TemplateBrowserSkeleton />
        )}
      </div>
    </section>
  );
}
